/* =============================================
   PLAXEL LUXE — Animations & Interactions
============================================= */

(function () {
  'use strict';

  function initLuxe() {
    const hasGSAP  = typeof gsap  !== 'undefined';
    const hasLenis = typeof Lenis !== 'undefined';

    /* ── Decorative video posters ─────────────────
       Shopify's video_tag filter auto-injects an <img> poster with no alt
       (hero + video sections). They're decorative (the video plays over them),
       so give them an empty alt to satisfy a11y / Lighthouse image-alt. */
    document.querySelectorAll('video > img:not([alt])').forEach(function (img) {
      img.setAttribute('alt', '');
    });

    /* ── Smooth scroll (Lenis) ────────────────── */
    let lenis;
    if (hasLenis) {
      lenis = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });
      window.lenis = lenis;

      if (hasGSAP && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
        lenis.on('scroll', ScrollTrigger.update);
        gsap.ticker.add((time) => { lenis.raf(time * 1000); });
        gsap.ticker.lagSmoothing(0);
      } else {
        function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
        requestAnimationFrame(raf);
      }

      const navPill = document.querySelector('.nav-pill');
      if (navPill) {
        lenis.on('scroll', ({ scroll }) => {
          navPill.style.background = scroll > 60
            ? 'rgba(13,12,10,0.88)'
            : 'rgba(13,12,10,0.72)';
        });
      }

      /* ── Keep Lenis scroll bounds in sync with late content growth ──
         Lenis caches the page height at init (DOMContentLoaded). Lazy images
         (e.g. the footer CTA device) and the web-font swap grow the page AFTER
         that, leaving Lenis clamped to a too-short limit so the bottom of the
         page becomes unreachable. Re-measure on load, font swap, lazy-image
         load, and any body size change. */
      const recomputeScroll = () => {
        if (lenis && typeof lenis.resize === 'function') lenis.resize();
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
      };
      window.addEventListener('load', recomputeScroll);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(recomputeScroll).catch(() => {});
      }
      document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        if (!img.complete) img.addEventListener('load', recomputeScroll, { once: true });
      });
      if ('ResizeObserver' in window) {
        let rzTimer;
        const ro = new ResizeObserver(() => {
          clearTimeout(rzTimer);
          rzTimer = setTimeout(recomputeScroll, 150);
        });
        ro.observe(document.body);
      }
    }

    /* ── Keep ScrollTrigger positions correct as late content grows the page ──
       Web-font swap and lazy images (e.g. the footer-CTA device) change layout
       AFTER ScrollTrigger computes its trigger positions, leaving scrub effects
       (footer device scale) miscalibrated. Re-measure on load, font swap, lazy-
       image load, and body resize. (This used to live in the Lenis block, which
       is now removed in favour of native scroll.) */
    if (hasGSAP && typeof ScrollTrigger !== 'undefined') {
      const refreshST = () => ScrollTrigger.refresh();
      window.addEventListener('load', refreshST);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(refreshST).catch(() => {});
      }
      document.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        if (!img.complete) img.addEventListener('load', refreshST, { once: true });
      });
      if ('ResizeObserver' in window) {
        let rzTimer;
        const ro = new ResizeObserver(() => {
          clearTimeout(rzTimer);
          rzTimer = setTimeout(refreshST, 150);
        });
        ro.observe(document.body);
      }
    }

    /* ── In-page anchor smooth scroll (works with or without Lenis) ── */
    document.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('a[href]') : null;
      if (!link) return;
      var href = link.getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) return;
      var target = document.getElementById(href.slice(1));
      if (!target) return;
      e.preventDefault();
      if (lenis && typeof lenis.scrollTo === 'function') {
        lenis.scrollTo(target, { offset: 0, duration: 1.2 });
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    /* ── Hero entrance (GSAP) ────────────────── */
    if (hasGSAP) {
      const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });
      if (document.querySelector('.hero-eyebrow'))
        tl.fromTo('.hero-eyebrow', { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.75 }, 0.15);
      if (document.querySelector('.line-inner'))
        tl.to('.line-inner', { y: '0%', duration: 1.0, stagger: 0.13, ease: 'power4.out' }, 0.35);
      if (document.getElementById('hero-sub'))
        tl.to('#hero-sub',     { opacity: 1, y: 0, duration: 0.85 }, 0.78);
      if (document.getElementById('hero-actions'))
        tl.to('#hero-actions', { opacity: 1, y: 0, duration: 0.75 }, 1.0);
      if (document.getElementById('hero-image-wrap'))
        tl.to('#hero-image-wrap', { opacity: 1, y: 0, duration: 1.0, ease: 'power3.out' }, 0.55);
    } else {
      ['#hero-sub','#hero-actions','#hero-image-wrap','.hero-eyebrow'].forEach(sel => {
        const el = document.querySelector(sel);
        if (el) { el.style.opacity = '1'; el.style.transform = 'none'; }
      });
      document.querySelectorAll('.line-inner').forEach(el => {
        el.style.transform = 'translateY(0%)';
      });
    }

    /* ── Scroll reveal (IntersectionObserver) ───── */
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const siblings = Array.from(
          entry.target.parentElement?.querySelectorAll('.reveal') || [entry.target]
        );
        const idx = siblings.indexOf(entry.target);
        setTimeout(
          () => entry.target.classList.add('visible'),
          Math.min(idx * 85, 240)
        );
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -50px 0px' });

    // Broaden the scroll-reveal beyond section headers to the main content
    // blocks (cards, rows) so the whole page flows in as you scroll.
    document.querySelectorAll('.plasma-card, .modality-card, .ba-card, .roi-row, .review-card')
      .forEach(function (el) { el.classList.add('reveal'); });

    document.querySelectorAll('.reveal').forEach(el => revealObserver.observe(el));

    /* ── 3D tilt on cards (GSAP) ───────────── */
    if (hasGSAP) {
      document.querySelectorAll('.modality-card .card-shell, .ba-card .card-shell').forEach(card => {
        card.addEventListener('mousemove', (e) => {
          const r = card.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width  - 0.5) * 6;
          const y = ((e.clientY - r.top)  / r.height - 0.5) * 6;
          gsap.to(card, {
            rotateX: -y, rotateY: x,
            duration: 0.35, ease: 'power2.out',
            transformPerspective: 900,
          });
        });
        card.addEventListener('mouseleave', () => {
          gsap.to(card, { rotateX: 0, rotateY: 0, duration: 0.55, ease: 'power3.out' });
        });
      });
    }

    /* ── Energy particle canvas (hero background) ─ */
    const canvas = document.getElementById('hero-canvas');
    if (canvas) {
      const ctx = canvas.getContext('2d');
      let W, H, animId;

      function resize() {
        W = canvas.width  = canvas.offsetWidth;
        H = canvas.height = canvas.offsetHeight;
      }
      resize();
      window.addEventListener('resize', resize, { passive: true });

      const pts = Array.from({ length: 50 }, () => ({
        x:  Math.random(),
        y:  Math.random(),
        r:  Math.random() * 1.4 + 0.2,
        vx: (Math.random() - 0.5) * 0.0003,
        vy: (Math.random() - 0.5) * 0.0003,
        a:  Math.random() * 0.3 + 0.05,
        da: (Math.random() > 0.5 ? 1 : -1) * 0.004,
      }));

      function tick() {
        ctx.clearRect(0, 0, W, H);
        pts.forEach(p => {
          p.x = (p.x + p.vx + 1) % 1;
          p.y = (p.y + p.vy + 1) % 1;
          p.a += p.da;
          if (p.a > 0.42 || p.a < 0.04) p.da *= -1;
          ctx.beginPath();
          ctx.arc(p.x * W, p.y * H, p.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(201,168,76,${p.a.toFixed(2)})`;
          ctx.fill();
        });
        animId = requestAnimationFrame(tick);
      }
      tick();

      const heroEl = document.getElementById('hero') || canvas.closest('section');
      if (heroEl) {
        const canvasObserver = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            if (!animId) tick();
          } else {
            cancelAnimationFrame(animId);
            animId = null;
          }
        }, { threshold: 0 });
        canvasObserver.observe(heroEl);
      }
    }

    /* ── Reduced motion fallback ─────────────── */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('visible'));
      ['#hero-sub','#hero-actions','#hero-image-wrap','.hero-eyebrow'].forEach(sel => {
        const el = document.querySelector(sel);
        if (el) { el.style.opacity = '1'; el.style.transform = 'none'; }
      });
      document.querySelectorAll('.line-inner').forEach(el => {
        el.style.transform = 'translateY(0%)';
      });
    }

    if (window.Shopify && window.Shopify.designMode) {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('visible'));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLuxe);
  } else {
    initLuxe();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target;
    if (root && root.querySelectorAll) {
      root.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('visible'); });
    }
  });

})();
